/**
 * Centralized TanStack Query key registry.
 *
 * One entry per backend resource. Each entry exposes the union of keys
 * actually used by the hooks that own that resource. Functions return
 * `as const` tuples so TanStack picks up the literal types.
 *
 * Convention: hook files own their resource. Avoid building keys inline
 * — always go through this registry so cross-resource invalidation
 * (e.g. picking → inventory) stays consistent.
 */

export const queryKeys = {
  // ------------------------------------------------------------------
  // Action log (audit trail)
  // ------------------------------------------------------------------
  actionLog: {
    all: ['action-log'] as const,
    list: (filters?: unknown) => ['action-log', filters] as const,
    detail: (id: string | null) => ['action-log-detail', id] as const,
    actionTypes: () => ['action-log-action-types'] as const,
    actors: () => ['action-log-actors'] as const,
    durations: (params?: unknown) => ['action-log-durations', params] as const,
    durationsDaily: (params?: unknown) =>
      ['action-log-durations-daily', params] as const,
    myProductivity: (params?: unknown) =>
      ['action-log-my-productivity', params] as const,
  },

  // ------------------------------------------------------------------
  // Adjustments
  // ------------------------------------------------------------------
  adjustments: {
    all: ['adjustments'] as const,
    list: (params?: unknown) => ['adjustments', params] as const,
    detail: (id: string | undefined) => ['adjustments', id] as const,
  },

  // ------------------------------------------------------------------
  // AI assistant (owner-gated agent)
  // ------------------------------------------------------------------
  aiAssistant: {
    all: ['ai-assistant'] as const,
    access: () => ['ai-assistant', 'access'] as const,
  },

  // ------------------------------------------------------------------
  // Allocation hierarchy (cross-resource invalidation target)
  // ------------------------------------------------------------------
  allocationHierarchy: {
    all: ['allocation-hierarchy'] as const,
  },

  // ------------------------------------------------------------------
  // Allocation priority rules (unified rules of type ALLOCATION_PRIORITY)
  // ------------------------------------------------------------------
  allocationPriorityRules: {
    all: ['allocation-priority-rules'] as const,
    list: (params?: unknown) => ['allocation-priority-rules', params] as const,
    detail: (id: string | undefined) =>
      ['allocation-priority-rules', 'detail', id] as const,
  },

  // ------------------------------------------------------------------
  // Allocation queue
  // ------------------------------------------------------------------
  allocationQueue: {
    all: ['allocation-queue'] as const,
    list: (filters?: unknown) => ['allocation-queue', filters] as const,
    entry: (id: string | undefined) =>
      ['allocation-queue', 'entry', id] as const,
    reallocationStatus: (warehouseId?: string) =>
      warehouseId === undefined
        ? (['allocation-queue', 'reallocation-status'] as const)
        : (['allocation-queue', 'reallocation-status', warehouseId] as const),
  },

  // ------------------------------------------------------------------
  // Allocation settings
  // ------------------------------------------------------------------
  allocationSettings: {
    all: ['allocation-settings'] as const,
    overview: (warehouseId?: string) =>
      ['allocation-settings', warehouseId] as const,
    schedule: (warehouseId?: string) =>
      ['allocation-settings', 'schedule', warehouseId] as const,
    staleOrder: (warehouseId?: string) =>
      ['allocation-settings', 'stale-order', warehouseId] as const,
  },

  // ------------------------------------------------------------------
  // Auto-tag rules (unified rules of type AUTO_TAG)
  // ------------------------------------------------------------------
  autoTagRules: {
    all: ['auto-tag-rules'] as const,
    list: (params?: unknown) => ['auto-tag-rules', params] as const,
  },

  // ------------------------------------------------------------------
  // Reference-number labels observed on recent sales orders (condition picker)
  // ------------------------------------------------------------------
  referenceNumberNames: {
    all: ['reference-number-names'] as const,
  },

  // ------------------------------------------------------------------
  // Claim-eligibility rules (unified rules of type CLAIM_ELIGIBILITY)
  // ------------------------------------------------------------------
  claimEligibilityRules: {
    all: ['claim-eligibility-rules'] as const,
    list: (params?: unknown) => ['claim-eligibility-rules', params] as const,
  },

  // ------------------------------------------------------------------
  // BOM (read-only — Acumatica is source of truth)
  // ------------------------------------------------------------------
  bom: {
    all: ['bom'] as const,
    detail: (kitItemId: string | undefined) => ['bom', kitItemId] as const,
    explosion: (kitItemId: string | undefined) =>
      ['bom-explosion', kitItemId] as const,
  },

  // ------------------------------------------------------------------
  // Bulk inventory adjustment (export + upload/validate + apply)
  // ------------------------------------------------------------------
  bulkInventoryAdjustment: {
    all: (params?: unknown) => ['bulkInventoryAdjustment', params] as const,
    job: (jobId: string) => ['bulkInventoryAdjustment', 'job', jobId] as const,
    preview: (
      jobId: string,
      params?: { page?: number; limit?: number; filter?: string },
    ) => ['bulkInventoryAdjustment', 'preview', jobId, params] as const,
    jobs: (params?: { warehouseId?: string; limit?: number }) =>
      ['bulkInventoryAdjustment', 'jobs', params] as const,
  },

  // ------------------------------------------------------------------
  // Bulk imports — unified history + rollback (covers ADJUST + RECEIVE +
  // ROLLBACK rows on the shared BulkInventoryAdjustmentJob table). Distinct
  // from `bulkInventoryAdjustment` / `bulkInventoryReceive` so the unified
  // history page can be invalidated independently of the feature pages.
  // ------------------------------------------------------------------
  bulkImports: {
    all: ['bulkImports'] as const,
    list: (params?: unknown) => ['bulkImports', 'list', params] as const,
    job: (jobId: string) => ['bulkImports', 'job', jobId] as const,
    preview: (
      jobId: string,
      params?: { page?: number; limit?: number; filter?: string },
    ) => ['bulkImports', 'preview', jobId, params] as const,
    rollbackEligibility: (jobId: string) =>
      ['bulkImports', 'rollback-eligibility', jobId] as const,
  },

  // ------------------------------------------------------------------
  // Bulk inventory receive (paste/CSV/XLSX → validate → apply)
  // Sibling of bulkInventoryAdjustment; both use the same shared
  // BulkInventoryAdjustmentJob table on the backend but the controller
  // surface + DTOs differ, so the keys stay distinct.
  // ------------------------------------------------------------------
  bulkInventoryReceive: {
    all: (params?: unknown) => ['bulkInventoryReceive', params] as const,
    templateMeta: (warehouseId: string | undefined) =>
      ['bulkInventoryReceive', 'template-meta', warehouseId] as const,
    job: (jobId: string) => ['bulkInventoryReceive', 'job', jobId] as const,
    preview: (
      jobId: string,
      params?: { page?: number; limit?: number; filter?: string },
    ) => ['bulkInventoryReceive', 'preview', jobId, params] as const,
    jobs: (params?: {
      warehouseId?: string;
      limit?: number;
      sinceDays?: number;
    }) => ['bulkInventoryReceive', 'jobs', params] as const,
  },

  // ------------------------------------------------------------------
  // Bulk inventory move (pick-from-bin / paste / CSV of sku,qty + batch
  // FROM/TO bin → validate → apply bin-to-bin RELOCATION). Sibling of
  // bulkInventoryReceive; same shared BulkInventoryAdjustmentJob table on
  // the backend (jobType=MOVE) but distinct controller surface + DTOs, so
  // the keys stay distinct.
  // ------------------------------------------------------------------
  bulkInventoryMove: {
    all: (params?: unknown) => ['bulkInventoryMove', params] as const,
    binContents: (
      warehouseId: string | undefined,
      locationId: string | null | undefined,
    ) =>
      ['bulkInventoryMove', 'bin-contents', warehouseId, locationId] as const,
    /** Prefix key — an applied move changes what BOTH bins hold. */
    binContentsAll: () => ['bulkInventoryMove', 'bin-contents'] as const,
    job: (jobId: string) => ['bulkInventoryMove', 'job', jobId] as const,
    preview: (
      jobId: string,
      params?: { page?: number; limit?: number; filter?: string },
    ) => ['bulkInventoryMove', 'preview', jobId, params] as const,
    jobs: (params?: {
      warehouseId?: string;
      limit?: number;
      sinceDays?: number;
    }) => ['bulkInventoryMove', 'jobs', params] as const,
  },

  // ------------------------------------------------------------------
  // Chat (real-time + REST history)
  // ------------------------------------------------------------------
  chat: {
    all: ['chat'] as const,
    channels: () => ['chat', 'channels'] as const,
    channelMessages: (channelId: string) =>
      ['chat', 'channel', channelId, 'messages'] as const,
    poMessages: (poId: string) => ['chat', 'po', poId, 'messages'] as const,
    soMessages: (soId: string) => ['chat', 'so', soId, 'messages'] as const,
    orderMessageCount: (type: 'po' | 'so', id: string) =>
      ['chat', type, id, 'message-count'] as const,
    channelMembers: (channelId: string) =>
      ['chat', 'channel', channelId, 'members'] as const,
    disabled: () => ['chat', 'disabled'] as const,
  },

  // ------------------------------------------------------------------
  // Chat notifications
  // ------------------------------------------------------------------
  chatNotifications: {
    all: ['chat-notifications'] as const,
    list: (filters?: unknown) =>
      ['chat-notifications', 'list', filters] as const,
    unreadCount: () => ['chat-notifications', 'unread-count'] as const,
  },

  // ------------------------------------------------------------------
  // Count batches (the manager/admin surface). Distinct from `counting`,
  // the counter-facing surface.
  // ------------------------------------------------------------------
  countBatches: {
    /** Prefix key — every batch query (list, detail, coverage, lines, tasks). */
    all: ['count-batches'] as const,
    list: (params?: unknown) => ['count-batches', 'list', params] as const,
    counts: (params?: unknown) => ['count-batches', 'counts', params] as const,
    detail: (id: string | undefined) =>
      ['count-batches', 'detail', id] as const,
    stages: (id: string | undefined) =>
      ['count-batches', 'stages', id] as const,
    coverage: (id: string | undefined) =>
      ['count-batches', 'coverage', id] as const,
    lines: (id: string | undefined, params?: unknown) =>
      ['count-batches', 'lines', id, params] as const,
    tasks: (id: string | undefined, params?: unknown) =>
      ['count-batches', 'tasks', id, params] as const,
    frozenBins: (id: string | undefined, stageId: string | undefined) =>
      ['count-batches', 'frozen-bins', id, stageId] as const,
    approvalJob: (id: string | undefined, jobId: string | undefined) =>
      ['count-batches', 'approval-job', id, jobId] as const,
    erpPushes: (id: string | undefined) =>
      ['count-batches', 'erp-pushes', id] as const,
    /** Import job history for one batch (50 newest). */
    importJobs: (id: string | undefined) =>
      ['count-batches', 'import-jobs', id] as const,
    importJob: (id: string | undefined, jobId: string | undefined) =>
      ['count-batches', 'import-job', id, jobId] as const,
    /** Omitting `params` yields the 4-element PREFIX, which partial-matches
     *  every page/filter variant — appending an explicit `undefined` slot
     *  would not (partialMatchKey compares that slot against the object). */
    importPreview: (
      id: string | undefined,
      jobId: string | undefined,
      params?: unknown,
    ) =>
      params === undefined
        ? (['count-batches', 'import-preview', id, jobId] as const)
        : (['count-batches', 'import-preview', id, jobId, params] as const),
  },

  // ------------------------------------------------------------------
  // Counting (the counter-facing surface).
  // ------------------------------------------------------------------
  counting: {
    /** Prefix key — the held-task list of any warehouse. */
    myTasksAll: () => ['counting', 'my-tasks'] as const,
    myTasks: (warehouseId: string | undefined) =>
      ['counting', 'my-tasks', warehouseId] as const,
    task: (taskId: string | undefined) => ['counting', 'task', taskId] as const,
    /** Prefix key — the assignment overview of any warehouse. */
    assignmentAll: () => ['counting', 'assignment'] as const,
    assignment: (warehouseId: string | undefined) =>
      ['counting', 'assignment', warehouseId] as const,
    /** Prefix key — every aisle drilldown of any warehouse/stage/aisle. */
    aisleAll: () => ['counting', 'aisle'] as const,
    /** `key` = the aisle string for AISLE scope, `'__loose__'` for LOOSE. */
    aisle: (warehouseId: string | undefined, stageId: string, key: string) =>
      ['counting', 'aisle', warehouseId, stageId, key] as const,
    taskSummary: (taskId: string) =>
      ['counting', 'task-summary', taskId] as const,
  },

  // ------------------------------------------------------------------
  // ERP customer / project lookups (used by criteria builder dropdowns)
  // ------------------------------------------------------------------
  customerLookups: {
    all: ['customer-lookups'] as const,
  },
  projectLookups: {
    all: ['project-lookups'] as const,
  },

  // ------------------------------------------------------------------
  // Dashboards
  // ------------------------------------------------------------------
  dashboardKpi: {
    all: ['dashboard-kpi'] as const,
    byWarehouse: (warehouseId?: string, range?: { from: string; to: string }) =>
      ['dashboard-kpi', warehouseId, range?.from, range?.to] as const,
  },

  ordersDashboard: {
    all: ['orders-dashboard'] as const,
    byWarehouse: (warehouseId?: string) =>
      ['orders-dashboard', warehouseId] as const,
  },

  // ------------------------------------------------------------------
  // Dock doors
  // ------------------------------------------------------------------
  dockDoors: {
    all: ['dock-doors'] as const,
    list: (filters?: unknown) => ['dock-doors', filters] as const,
    detail: (id: string) => ['dock-doors', id] as const,
  },

  // ------------------------------------------------------------------
  // Documents
  // ------------------------------------------------------------------
  documents: {
    all: ['documents'] as const,
    forEntity: (entityType: string, entityId: string | undefined) =>
      ['documents', entityType, entityId] as const,
  },

  // ------------------------------------------------------------------
  // Health
  // ------------------------------------------------------------------
  health: {
    all: ['health'] as const,
  },

  // ------------------------------------------------------------------
  // Bins (compat — most code uses locations; this preserves cache key
  // shape for legacy /api/bins/:id consumers)
  // ------------------------------------------------------------------
  bins: {
    all: ['bins'] as const,
    detail: (id: string) => ['bins', id] as const,
  },

  // ------------------------------------------------------------------
  // Inventory
  // ------------------------------------------------------------------
  inventory: {
    all: ['inventory'] as const,
    onHand: (filters?: unknown) => ['inventory', 'on-hand', filters] as const,
    onHandAggregated: (filters?: unknown) =>
      ['inventory', 'on-hand-aggregated', filters] as const,
    onHandStockCounts: (filters?: unknown) =>
      ['inventory', 'on-hand-stock-counts', filters] as const,
    reconciliation: (filters?: unknown) =>
      ['inventory', 'reconciliation', filters] as const,
    ledger: (filters?: unknown) => ['inventory', 'ledger', filters] as const,
    summary: (warehouseId?: string) =>
      ['inventory', 'summary', warehouseId] as const,
    itemDetail: (
      itemId: string | undefined,
      warehouseId: string | undefined,
      binScopeIn?: string,
    ) => ['inventory', 'item-detail', itemId, warehouseId, binScopeIn] as const,
  },

  // ------------------------------------------------------------------
  // Items
  // ------------------------------------------------------------------
  items: {
    all: ['items'] as const,
    list: (filters?: unknown) => ['items', filters] as const,
    detail: (id: string | undefined) => ['items', id] as const,
    usage: (id: string | undefined, kind: string, params?: unknown) =>
      ['items', id, 'usage', kind, params] as const,
  },

  // ------------------------------------------------------------------
  // Kit assembly
  // ------------------------------------------------------------------
  kitAssembly: {
    all: ['kit-assembly'] as const,
    list: (params?: unknown) => ['kit-assembly', 'list', params] as const,
    detail: (id: string | undefined) => ['kit-assembly', 'detail', id] as const,
    suggestSources: (
      kitItemId: string | undefined,
      warehouseId: string | undefined,
      quantity: number | undefined,
    ) =>
      [
        'kit-assembly',
        'suggest-sources',
        kitItemId,
        warehouseId,
        quantity,
      ] as const,
  },

  // ------------------------------------------------------------------
  // Kit items (catalog of STOCK_KIT / NON_STOCK_KIT items)
  // ------------------------------------------------------------------
  kitItems: {
    all: ['kit-items'] as const,
    list: (
      itemType: string | undefined,
      search: string | undefined,
      /** `excludeItemClasses` csv; undefined = none excluded. */
      excludeItemClasses?: string,
    ) => ['kit-items', itemType, search, excludeItemClasses] as const,
  },

  // ------------------------------------------------------------------
  // Kit parts
  // ------------------------------------------------------------------
  kitParts: {
    all: ['kit-parts'] as const,
    bySku: (sku: string) => ['kit-parts', sku] as const,
    distinctSkus: () => ['kit-parts', 'distinct-skus'] as const,
    distinctColors: () => ['kit-parts', 'distinct-colors'] as const,
  },

  // ------------------------------------------------------------------
  // Kit putaway
  // ------------------------------------------------------------------
  kitPutaway: {
    all: ['kit-putaway'] as const,
    list: (params?: unknown) => ['kit-putaway', 'list', params] as const,
    targetBins: (warehouseId?: string) =>
      ['kit-putaway', 'target-bins', warehouseId] as const,
    /** Drill-down: the assemblies behind one SKU × bin aggregate row. */
    assemblies: (warehouseId?: string, itemId?: string, locationId?: string) =>
      ['kit-putaway', 'assemblies', warehouseId, itemId, locationId] as const,
    /** Assembly-grain pending list (grid view: one card per assembly). */
    pendingAssemblies: (params?: unknown) =>
      ['kit-putaway', 'pending-assemblies', params] as const,
  },

  // ------------------------------------------------------------------
  // Locations
  // ------------------------------------------------------------------
  locations: {
    all: ['locations'] as const,
    list: (filters?: unknown) => ['locations', filters] as const,
    detail: (id: string | undefined) => ['locations', id] as const,
    zones: (warehouseId: string | undefined) =>
      ['locations', 'zones', warehouseId] as const,
  },

  // ------------------------------------------------------------------
  // Layout change requests (Google Sheets plugin submissions → manager review)
  // ------------------------------------------------------------------
  layoutChangeRequests: {
    all: ['layout-change-requests'] as const,
    list: (filters?: unknown) => ['layout-change-requests', filters] as const,
    pendingCount: () => ['layout-change-requests', 'pending-count'] as const,
  },

  // ------------------------------------------------------------------
  // Manufacturing dashboard
  // ------------------------------------------------------------------
  manufacturingDashboard: {
    all: ['manufacturing-dashboard'] as const,
    list: (
      historyWeeks: number | undefined,
      warehouseId: string | null | undefined,
      /** Page-level channel (`ALL` or a slug); undefined = header applies. */
      channel?: string,
    ) =>
      ['manufacturing-dashboard', historyWeeks, warehouseId, channel] as const,
  },
  skuDelayNotes: {
    history: (sku: string) => ['sku-delay-notes', 'history', sku] as const,
  },
  // mfg-program PR-A — its own root: patchKitRows() rewrites every query
  // under manufacturingDashboard.all assuming a dashboard payload.
  manufacturingSettings: {
    all: ['manufacturing-settings'] as const,
  },
  // mfg-program PR-C — the kit row's SKU Needs numbers and drill-down. Own
  // root for the same reason as manufacturingSettings: patchKitRows() rewrites
  // every query under manufacturingDashboard.all as a dashboard payload.
  manufacturingDashboardSkuNeeds: {
    all: ['manufacturing-dashboard-sku-needs'] as const,
    /** `GET /manufacturing-dashboard/sku-needs-summary` for one window. */
    summary: (
      warehouseId: string | null | undefined,
      channel: string,
      sortedSkus: readonly string[],
    ) =>
      [
        'manufacturing-dashboard-sku-needs',
        'summary',
        warehouseId,
        channel,
        sortedSkus,
      ] as const,
    /** `GET /manufacturing-dashboard/sku-needs-orders` (row drill-down). */
    orders: (
      sku: string,
      warehouseId: string | undefined,
      channel: string | undefined,
      orderNumbers: string | undefined,
      universe: 'unallocated' | 'short' | 'both' | undefined,
      includeNonStock: boolean | undefined,
      onHoldMode: 'include' | 'only' | 'exclude' | undefined,
    ) =>
      [
        'manufacturing-dashboard-sku-needs',
        'orders',
        sku,
        warehouseId,
        channel,
        orderNumbers,
        universe,
        includeNonStock,
        onHoldMode,
      ] as const,
  },

  // ------------------------------------------------------------------
  // Manufacturing work orders
  // ------------------------------------------------------------------
  manufacturingWorkOrders: {
    all: ['manufacturing-work-orders'] as const,
    // Feature switches (saw labels on/off).
    features: ['manufacturing-work-orders', 'features'] as const,
    // End-of-line count (2026-10-30): what open orders still need, and the latest counts.
    endCountNeeds: ['manufacturing-work-orders', 'end-count', 'needs'] as const,
    endCountRecent: [
      'manufacturing-work-orders',
      'end-count',
      'recent',
    ] as const,
    list: (query?: unknown) => ['manufacturing-work-orders', query] as const,
    counts: (query?: unknown) =>
      ['manufacturing-work-orders', 'counts', query] as const,
    // mfg-program PR-F. KM-5: a kitchen route's read of the same id is its
    // own entry (`line` = 'KITCHEN'); closet reads keep the two-part key.
    detail: (id: string, line?: string) =>
      line
        ? (['manufacturing-work-orders', 'detail', id, line] as const)
        : (['manufacturing-work-orders', 'detail', id] as const),
    // mfg-program PR-J
    partLines: (query?: unknown) =>
      ['manufacturing-work-orders', 'part-lines', query] as const,
    partLineCounts: (query?: unknown) =>
      ['manufacturing-work-orders', 'part-lines-counts', query] as const,
    // manufacturing v3 part 8: the unassigned-parts pool (refreshed with the rest)
    partPool: (query?: unknown) =>
      ['manufacturing-work-orders', 'part-pool', query] as const,
    partPoolLots: (key?: unknown) =>
      ['manufacturing-work-orders', 'part-pool-lots', key] as const,
    canCreate: (warehouseId: string | null) =>
      ['manufacturing-work-orders', 'can-create', warehouseId] as const,
    creators: ['manufacturing-work-orders', 'creators'] as const,
    people: (warehouseId: string | null, productLine?: string) =>
      productLine
        ? ([
            'manufacturing-work-orders',
            'people',
            warehouseId,
            productLine,
          ] as const)
        : (['manufacturing-work-orders', 'people', warehouseId] as const),
    // mfg-program PR-I
    combinePreview: (dto: unknown) =>
      ['manufacturing-work-orders', 'combine-preview', dto] as const,
    // mfg-v2 V2-D
    queueLines: (query?: unknown) =>
      ['manufacturing-work-orders', 'queue-lines', query] as const,
    queueLineCounts: (query?: unknown) =>
      ['manufacturing-work-orders', 'queue-lines-counts', query] as const,
    // manufacturing v3: the Record parts sheet of one saw line
    queueLineParts: (queueLineId: string | null) =>
      ['manufacturing-work-orders', 'queue-line-parts', queueLineId] as const,
  },
  // mfg-program PR-G
  manufacturingMachines: {
    all: ['manufacturing-machines'] as const,
    list: (
      warehouseId: string | null | undefined,
      includeInactive: boolean,
      type?: string,
    ) =>
      [
        'manufacturing-machines',
        warehouseId,
        includeInactive,
        type ?? null,
      ] as const,
    // mfg-program PR-G
    labelPrinters: (warehouseId: string | null | undefined) =>
      ['manufacturing-machines', 'label-printers', warehouseId] as const,
  },
  // Settings → Manufacturing "Kits without parts" (2026-10-20)
  kitsWithoutRecipe: {
    all: ['kits-without-recipe'] as const,
    list: (params: unknown) => ['kits-without-recipe', 'list', params] as const,
  },
  // mfg-program PR-G
  manufacturingPartPrograms: {
    all: ['manufacturing-part-programs'] as const,
    list: (params: unknown) =>
      ['manufacturing-part-programs', 'list', params] as const,
    unmapped: (params: unknown) =>
      ['manufacturing-part-programs', 'unmapped', params] as const,
  },

  // ------------------------------------------------------------------
  // Notifications
  // ------------------------------------------------------------------
  notifications: {
    all: ['notifications'] as const,
    lists: () => ['notifications', 'list'] as const,
    list: (filters: unknown) => ['notifications', 'list', filters] as const,
    detail: (id: string) => ['notifications', id] as const,
    unreadCount: (warehouseId?: string) =>
      ['notifications', 'unread', warehouseId] as const,
    combinedUnreadCount: (warehouseId?: string) =>
      ['notifications', 'combinedUnreadCount', warehouseId] as const,
  },

  // ------------------------------------------------------------------
  // Alert preferences (per-user notification-type opt-in/out)
  // ------------------------------------------------------------------
  alertPreferences: {
    all: ['alert-preferences'] as const,
    list: () => ['alert-preferences', 'list'] as const,
    /** Another user's preferences, read by an admin (Edit User dialog). */
    forUser: (userId: string) => ['alert-preferences', 'user', userId] as const,
  },

  // ------------------------------------------------------------------
  // Push subscriptions (per-user registered browser/desktop FCM devices)
  // ------------------------------------------------------------------
  pushSubscriptions: {
    all: ['push-subscriptions'] as const,
    list: () => ['push-subscriptions', 'list'] as const,
  },

  // ------------------------------------------------------------------
  // Order hold rules (AUTO time-bound hold rules)
  // ------------------------------------------------------------------
  orderHoldRules: {
    all: ['order-hold-rules'] as const,
    list: (filter?: unknown) => ['order-hold-rules', filter] as const,
    detail: (id: string | undefined) =>
      ['order-hold-rules', 'detail', id] as const,
  },

  // ------------------------------------------------------------------
  // Holiday / blackout calendar (order-hold module)
  // ------------------------------------------------------------------
  holidayCalendar: {
    all: ['holiday-calendar'] as const,
    list: (filter?: unknown) => ['holiday-calendar', filter] as const,
  },

  // ------------------------------------------------------------------
  // Order holds (currently-held sales orders, grouped from active holds)
  // ------------------------------------------------------------------
  orderHolds: {
    all: ['order-holds'] as const,
    held: (params?: unknown) => ['order-holds', 'held', params] as const,
    pending: (params?: unknown) => ['order-holds', 'pending', params] as const,
  },

  // ------------------------------------------------------------------
  // Pick orders (and pickers)
  // ------------------------------------------------------------------
  gatherLists: {
    all: ['gather-lists'] as const,
    /**
     * `params` carries the status filter and paging: the SERVER decides which
     * lists the working view shows, so two filters are two cache entries.
     */
    list: (warehouseId?: string, params?: unknown) =>
      ['gather-lists', 'list', warehouseId, params] as const,
    /** Distinct from `list` — a different endpoint returning a different set. */
    mine: (warehouseId?: string) =>
      ['gather-lists', 'mine', warehouseId] as const,
    /**
     * `listIds` scopes coverage to one manager's batch, and belongs in the key
     * because the answer genuinely differs per batch — sharing one entry would
     * let a warehouse-wide "not covered" verdict disarm a scoped Release.
     */
    coverage: (warehouseId?: string, listIds?: readonly string[]) =>
      ['gather-lists', 'coverage', warehouseId, listIds ?? []] as const,
    pool: (params?: unknown) => ['gather-lists', 'pool', params] as const,
    /**
     * Every warehouse's groups at once — what a BIN edit invalidates. A bin
     * carries the drop desk (`isGatherStaging`) that `groups` reports as
     * `dropBinCode`, and the bin mutations do not know which warehouse's entry
     * is cached, so they refresh the lot. Also the PREFIX `groups` derives
     * from, so the two cannot drift apart.
     */
    groupsAll: () => ['gather-lists', 'groups'] as const,
    /**
     * The warehouse's configured order groups — per warehouse, because the
     * setting resolves GLOBAL→WAREHOUSE and two warehouses genuinely differ.
     */
    groups: (warehouseId?: string) =>
      [...queryKeys.gatherLists.groupsAll(), warehouseId] as const,
    detail: (id?: string) => ['gather-lists', 'detail', id] as const,
    /**
     * Every recipes entry for one warehouse — the PREFIX both modes below sit
     * under. Invalidating it (the settings card does, after every save)
     * refreshes the builder's entry and the card's own.
     */
    recipesAll: (warehouseId?: string) =>
      ['gather-lists', 'recipes', warehouseId] as const,
    /**
     * List recipes + definitions — per warehouse, resolved GLOBAL→WAREHOUSE.
     * The STRICT read (settings card) has its own entry so a degraded builder
     * answer can never be served to it as saved fact.
     */
    recipes: (warehouseId?: string, strict = false) =>
      [
        ...queryKeys.gatherLists.recipesAll(warehouseId),
        strict ? 'strict' : 'degrading',
      ] as const,
  },
  // ------------------------------------------------------------------
  // Gather (phase-2 parcel picking — the gatherer's scan loop)
  //
  // `gather-lookup`, the gather POST and the undo DELETE are mutations and
  // need no keys; the gather HISTORY is a query and does.
  // ------------------------------------------------------------------
  gather: {
    all: ['gather'] as const,
    /**
     * The drop desk, per warehouse AND per order group — a warehouse has one
     * desk for each group, so two groups must never share a cache entry.
     * `null` for "no group named", which the server reads as the parcel desk.
     */
    stagingLocations: (warehouseId?: string, gatherGroup?: string) =>
      [
        'gather',
        'staging-locations',
        warehouseId,
        gatherGroup ?? null,
      ] as const,
    history: (listId?: string) => ['gather', 'history', listId] as const,
    /**
     * Skipped-scan report (managers): GET /gather-lists/scan-skips. Gather's
     * OWN report — `scanSkips` next door is picking's and the two never share
     * a cache entry. Nested under `gather.all` on purpose: recording a gather
     * changes these counts, so the existing gather invalidation refreshes it.
     */
    scanSkips: (params?: unknown) => ['gather', 'scan-skips', params] as const,
  },
  pickers: {
    all: ['pickers'] as const,
    list: (warehouseId?: string) => ['pickers', warehouseId] as const,
  },
  recounters: {
    all: ['recounters'] as const,
    list: (warehouseId?: string) => ['recounters', warehouseId] as const,
    strict: (warehouseId?: string) =>
      ['recounters', 'strict', warehouseId] as const,
  },
  wrappers: {
    all: ['wrappers'] as const,
    list: (warehouseId?: string) => ['wrappers', warehouseId] as const,
  },
  counters: {
    all: ['counters'] as const,
    list: (warehouseId?: string) => ['counters', warehouseId] as const,
  },
  pickOrders: {
    all: ['pick-orders'] as const,
    list: (filters?: unknown) => ['pick-orders', filters] as const,
    detail: (id: string | undefined) => ['pick-orders', id] as const,
    /** Second box labels (GET /picking/:id/box-labels). Under `detail` so any
     * invalidation of the order also refreshes the gate. */
    boxLabels: (id: string | undefined) =>
      ['pick-orders', id, 'box-labels'] as const,
  },
  /** Skipped-scan report (managers): GET /picking/scan-skips. */
  scanSkips: {
    all: ['scan-skips'] as const,
    list: (params?: unknown) => ['scan-skips', params] as const,
  },
  /** Per-unit scan ledger, keyed by PICK TASK (not by pick order). */
  unitScans: {
    all: ['unit-scans'] as const,
    byTask: (pickTaskId: string | undefined) =>
      ['unit-scans', pickTaskId] as const,
  },
  pickClaim: {
    all: ['pick-claim-depth'] as const,
    depth: (warehouseId?: string, filters?: unknown) =>
      ['pick-claim-depth', warehouseId, filters ?? null] as const,
  },
  shortPickExceptions: {
    all: ['short-pick-exceptions'] as const,
    list: (filters?: unknown) => ['short-pick-exceptions', filters] as const,
    detail: (id: string | undefined) => ['short-pick-exceptions', id] as const,
  },
  shortPickCounts: {
    all: ['short-pick-counts'] as const,
    forSalesOrder: (salesOrderId?: string) =>
      ['short-pick-counts', salesOrderId] as const,
  },
  // The /short-picks page reads short picks GROUPED BY SALES ORDER (one row per
  // SO, expandable to its exception rows) — not one row per exception.
  shortPickOrders: {
    all: ['short-pick-orders'] as const,
    list: (filters?: unknown) => ['short-pick-orders', filters] as const,
  },
  globalShortPickCounts: {
    all: ['global-short-pick-counts'] as const,
    list: (warehouseId?: string) =>
      ['global-short-pick-counts', warehouseId] as const,
  },
  unallocatedStock: {
    all: ['unallocated-stock'] as const,
    list: (
      itemId: string | undefined,
      warehouseId: string | undefined,
      excludeOnHandId: string | undefined,
    ) => ['unallocated-stock', itemId, warehouseId, excludeOnHandId] as const,
  },

  // ------------------------------------------------------------------
  // Pick split rules (unified rules of type PICK_SPLIT)
  // ------------------------------------------------------------------
  pickSplitRules: {
    all: ['pick-split-rules'] as const,
    list: (params?: unknown) => ['pick-split-rules', params] as const,
  },

  // ------------------------------------------------------------------
  // Channels (data-driven business channels — the `channels` table)
  // ------------------------------------------------------------------
  channels: {
    all: ['channels'] as const,
    list: (params?: { includeInactive?: boolean }) =>
      ['channels', params ?? {}] as const,
    accessible: (warehouseId?: string) =>
      ['channels', 'accessible', warehouseId] as const,
  },

  // ------------------------------------------------------------------
  // Channel rules (unified rules of type CHANNEL)
  // ------------------------------------------------------------------
  channelRules: {
    all: ['channel-rules'] as const,
    list: (params?: unknown) => ['channel-rules', params] as const,
  },

  // ------------------------------------------------------------------
  // Bin reservation rules (unified rules of type BIN_RESERVATION)
  // ------------------------------------------------------------------
  binReservationRules: {
    all: ['bin-reservation-rules'] as const,
    list: (params?: unknown) => ['bin-reservation-rules', params] as const,
  },

  // ------------------------------------------------------------------
  // Bin priority rules (unified rules of type BIN_PRIORITY)
  // ------------------------------------------------------------------
  binPriorityRules: {
    all: ['bin-priority-rules'] as const,
    list: (params?: unknown) => ['bin-priority-rules', params] as const,
  },

  // ------------------------------------------------------------------
  // Pick path sort rules (unified rules of type PICK_PATH_SORT)
  // ------------------------------------------------------------------
  pickPathSortRules: {
    all: ['pick-path-sort-rules'] as const,
    list: (params?: unknown) => ['pick-path-sort-rules', params] as const,
  },

  allocationStrategyRules: {
    all: ['allocation-strategy-rules'] as const,
    list: (params?: unknown) => ['allocation-strategy-rules', params] as const,
  },

  // ------------------------------------------------------------------
  // Purchase orders + receipts
  // ------------------------------------------------------------------
  purchaseOrders: {
    all: ['purchase-orders'] as const,
    list: (filters?: unknown) => ['purchase-orders', filters] as const,
    detail: (id: string | undefined) => ['purchase-orders', id] as const,
    counts: (filters?: unknown) =>
      ['purchase-orders', 'counts', filters] as const,
  },
  poReceipts: {
    all: ['po-receipts'] as const,
    forPo: (purchaseOrderId: string) =>
      ['po-receipts', purchaseOrderId] as const,
  },

  // ------------------------------------------------------------------
  // Putaway tasks
  // ------------------------------------------------------------------
  putaway: {
    all: ['putaway'] as const,
    list: (filters?: unknown) => ['putaway', filters] as const,
    suggestions: (taskId: string | undefined) =>
      ['putaway', 'suggestions', taskId] as const,
  },

  // ------------------------------------------------------------------
  // Putaway rules
  // ------------------------------------------------------------------
  putawayRules: {
    all: ['putaway-rules'] as const,
    list: (params?: unknown) => ['putaway-rules', params] as const,
    detail: (id: string | undefined) => ['putaway-rules', id] as const,
    match: (params?: unknown) => ['putaway-rules', 'match', params] as const,
  },

  // ------------------------------------------------------------------
  // Recounts
  // ------------------------------------------------------------------
  recounts: {
    all: ['recounts'] as const,
    list: (filters?: unknown) => ['recounts', filters] as const,
    detail: (id: string | undefined) => ['recounts', id] as const,
    /** Short-pick fetch list of one recount (short-pick plan §3.5.3). */
    fetchList: (id: string | undefined) =>
      ['recounts', id, 'fetch-list'] as const,
  },

  // ------------------------------------------------------------------
  // Wrap orders (wrapping step between RECOUNTED and COMPLETED)
  // ------------------------------------------------------------------
  wrapping: {
    all: ['wrapping'] as const,
    list: (filters?: unknown) => ['wrapping', filters] as const,
    detail: (id: string | undefined) => ['wrapping', id] as const,
    forSalesOrder: (salesOrderId: string | undefined) =>
      ['wrapping', 'for-sales-order', salesOrderId] as const,
    palletPhotos: (search?: string) =>
      ['wrapping', 'pallet-photos', search ?? ''] as const,
  },

  // ------------------------------------------------------------------
  // Reports
  // ------------------------------------------------------------------
  reports: {
    all: ['reports'] as const,
    inventorySummary: (warehouseId: string) =>
      ['reports', 'inventory-summary', warehouseId] as const,
    inventoryByItem: (
      warehouseId: string,
      skip: number | undefined,
      take: number | undefined,
    ) => ['reports', 'inventory-by-item', warehouseId, { skip, take }] as const,
    activitySummary: (
      warehouseId: string,
      startDate: string,
      endDate: string,
    ) =>
      ['reports', 'activity-summary', warehouseId, startDate, endDate] as const,
    transactions: (filters: unknown) =>
      ['reports', 'transactions', filters] as const,
    dashboardKpi: (warehouseId: string) =>
      ['reports', 'dashboard-kpi', warehouseId] as const,
    trends: (warehouseId: string, days: number) =>
      ['reports', 'trends', warehouseId, days] as const,
    // ShipHawk sync reconciliation report (global; warehouse-agnostic)
    shiphawkSync: {
      summary: () => ['reports', 'shiphawk-sync', 'summary'] as const,
      bucketA: (params: {
        skip?: number;
        take?: number;
        sourceSystem?: string;
      }) => ['reports', 'shiphawk-sync', 'bucket-a', params] as const,
      bucketB: (params: {
        skip?: number;
        take?: number;
        callbackOutcome?: string;
      }) => ['reports', 'shiphawk-sync', 'bucket-b', params] as const,
    },
  },

  // ------------------------------------------------------------------
  // Sales orders
  // ------------------------------------------------------------------
  salesOrders: {
    all: ['sales-orders'] as const,
    list: (filters?: unknown) => ['sales-orders', filters] as const,
    detail: (id: string | undefined) => ['sales-orders', id] as const,
    statusCounts: (filters?: unknown) =>
      ['sales-orders', 'status-counts', filters] as const,
    statuses: () => ['sales-orders', 'statuses'] as const,
    transitions: (id: string | undefined) =>
      ['sales-orders', id, 'transitions'] as const,
    timeline: (id: string | undefined) =>
      ['sales-orders', id, 'timeline'] as const,
    sources: (id: string | undefined) =>
      ['sales-orders', id, 'sources'] as const,
    csNotes: (id: string | undefined) =>
      ['sales-orders', id, 'cs-notes'] as const,
  },
  splitPreview: {
    all: ['split-preview'] as const,
    forOrder: (orderId: string | null) => ['split-preview', orderId] as const,
  },

  // ------------------------------------------------------------------
  // Saved views (personal per-user, per-page table/filter presets)
  // ------------------------------------------------------------------
  savedViews: {
    all: ['saved-views'] as const,
    list: (pageKey: string) => ['saved-views', 'list', pageKey] as const,
  },

  // ------------------------------------------------------------------
  // Table column policies (dashboard column visibility levels)
  // ------------------------------------------------------------------
  tableColumnPolicies: {
    all: ['table-column-policies'] as const,
  },

  // ------------------------------------------------------------------
  // Sessions (auth)
  // ------------------------------------------------------------------
  sessions: {
    all: ['sessions'] as const,
    me: () => ['sessions', 'me'] as const,
  },

  // ------------------------------------------------------------------
  // IP allowlist (global CIDR rules; gates email + username login,
  // Google sign-in bypasses)
  // ------------------------------------------------------------------
  ipAllowlist: {
    all: ['ip-allowlist'] as const,
    list: () => ['ip-allowlist', 'list'] as const,
  },

  // ------------------------------------------------------------------
  // Printing (PrintNode-relayed thermal labels)
  // ------------------------------------------------------------------
  printers: {
    all: ['printers'] as const,
    list: () => ['printers', 'list'] as const,
    /**
     * Capability probe for the design-assembly control (§M.shopify). Folded in
     * here rather than a `printing` namespace of its own: a second
     * printing-adjacent top-level group holding one key is easy to reach for
     * wrongly next to `printers`, and its `all` would have been dead.
     */
    designAssemblyEnabled: () =>
      ['printers', 'design-assembly', 'enabled'] as const,
  },
  printJobs: {
    all: ['print-jobs'] as const,
    list: (printerId?: string, limit?: number) =>
      ['print-jobs', 'list', printerId, limit] as const,
  },
  labelTemplates: {
    all: ['label-templates'] as const,
    // The WHOLE param object, like labelSerials.list below. Keying on only
    // warehouseId+type let two callers differing solely by `activeOnly` or
    // `includeGlobal` read each other's cache — no collision today (both
    // typed callers pass activeOnly: true) but the first caller that lists a
    // type with and without inactive rows gets the other's result.
    list: (params: Record<string, unknown>) =>
      ['label-templates', 'list', params] as const,
    detail: (id: string) => ['label-templates', 'detail', id] as const,
    fieldCatalog: (type: string) =>
      ['label-templates', 'field-catalog', type] as const,
    presets: (type?: string, size?: string) =>
      ['label-templates', 'presets', type, size] as const,
  },

  labelSerials: {
    all: ['label-serials'] as const,
    // The whole filter object is the key: two lists differing only by date
    // range are different result sets, and collapsing them onto one key is
    // how a filtered list shows another filter's rows.
    list: (params: Record<string, unknown>) =>
      ['label-serials', 'list', params] as const,
    // Counters are fleet-global — one key, no warehouse dimension.
    sequences: () => ['label-serials', 'sequences'] as const,
  },

  // ------------------------------------------------------------------
  // Settings
  // ------------------------------------------------------------------
  settings: {
    all: ['settings'] as const,
    list: (params?: unknown) => ['settings', params] as const,
    detail: (id: string | undefined) => ['settings', id] as const,
    /** Effective `short_picks.fill_flow_enabled` for a warehouse (plan §3.3). */
    fillFlow: (warehouseId: string | null | undefined) =>
      ['settings', 'fill-flow', warehouseId] as const,
    resolve: (key: string | undefined, scopes: unknown) =>
      ['settings', 'resolve', key, scopes] as const,
  },

  // ------------------------------------------------------------------
  // SKU Needs dashboard
  // ------------------------------------------------------------------
  skuNeeds: {
    all: ['sku-needs'] as const,
    list: (
      search: string | undefined,
      productLineIn: string | undefined,
      materialTypeIn: string | undefined,
      itemTypeIn: string | undefined,
      colorIn: string | undefined,
      tagIdsIn: string | undefined,
      tagIdsNotIn: string | undefined,
      orderDateFrom: string | undefined,
      orderDateTo: string | undefined,
      requestedShipDateFrom: string | undefined,
      requestedShipDateTo: string | undefined,
      shipmentDateFrom: string | undefined,
      shipmentDateTo: string | undefined,
      orderNumbers: string | undefined,
      page: number,
      limit: number,
      warehouseId: string | undefined,
      sortBy: string | undefined,
      sortDir: 'asc' | 'desc' | undefined,
      universe: 'unallocated' | 'short' | 'both' | undefined,
      coverage: 'missing' | 'all' | undefined,
      includeCompleted: boolean | undefined,
      onHoldMode: 'include' | 'only' | 'exclude' | undefined,
      includeAcumatica: boolean | undefined,
      binScopeIn: string | undefined,
    ) =>
      [
        'sku-needs',
        'list',
        search,
        productLineIn,
        materialTypeIn,
        itemTypeIn,
        colorIn,
        tagIdsIn,
        tagIdsNotIn,
        orderDateFrom,
        orderDateTo,
        requestedShipDateFrom,
        requestedShipDateTo,
        shipmentDateFrom,
        shipmentDateTo,
        orderNumbers,
        page,
        limit,
        warehouseId,
        sortBy,
        sortDir,
        universe,
        coverage,
        includeCompleted,
        onHoldMode,
        includeAcumatica,
        binScopeIn,
      ] as const,
    orders: (
      sku: string | null,
      orderNumbers: string | undefined,
      warehouseId: string | undefined,
      universe: 'unallocated' | 'short' | 'both' | undefined,
      includeNonStock?: boolean,
      onHoldMode?: 'include' | 'only' | 'exclude',
    ) =>
      [
        'sku-needs',
        'orders',
        sku,
        orderNumbers,
        warehouseId,
        universe,
        includeNonStock,
        onHoldMode,
      ] as const,
    /**
     * Per-bin on-hand drill-down — `GET /api/sku-needs/bin-breakdown`.
     * Distinct prefix so it can never collide with the per-SKU orders
     * drill-down above.
     */
    binBreakdown: (
      sku: string,
      warehouseId: string | undefined,
      binScopeIn: string | undefined,
    ) => ['sku-needs', 'bin-breakdown', sku, warehouseId, binScopeIn] as const,
    /**
     * Orders view list — `GET /api/sku-needs/orders-list`. Distinct prefix
     * (`'orders-list'`) from the per-SKU drill-down (`'orders'`) so the two
     * caches cannot collide.
     */
    ordersList: (
      search: string | undefined,
      productLineIn: string | undefined,
      materialTypeIn: string | undefined,
      itemTypeIn: string | undefined,
      colorIn: string | undefined,
      tagIdsIn: string | undefined,
      tagIdsNotIn: string | undefined,
      orderDateFrom: string | undefined,
      orderDateTo: string | undefined,
      requestedShipDateFrom: string | undefined,
      requestedShipDateTo: string | undefined,
      shipmentDateFrom: string | undefined,
      shipmentDateTo: string | undefined,
      orderNumbers: string | undefined,
      page: number,
      limit: number,
      warehouseId: string | undefined,
      sortBy: string | undefined,
      sortDir: 'asc' | 'desc' | undefined,
      universe: 'unallocated' | 'short' | 'both' | undefined,
      coverage: 'missing' | 'all' | undefined,
      includeCompleted: boolean | undefined,
      onHoldMode: 'include' | 'only' | 'exclude' | undefined,
      includeAcumatica: boolean | undefined,
      sourceFilter: 'all' | 'wms' | 'acumatica' | undefined,
      binScopeIn: string | undefined,
    ) =>
      [
        'sku-needs',
        'orders-list',
        search,
        productLineIn,
        materialTypeIn,
        itemTypeIn,
        colorIn,
        tagIdsIn,
        tagIdsNotIn,
        orderDateFrom,
        orderDateTo,
        requestedShipDateFrom,
        requestedShipDateTo,
        shipmentDateFrom,
        shipmentDateTo,
        orderNumbers,
        page,
        limit,
        warehouseId,
        sortBy,
        sortDir,
        universe,
        coverage,
        includeCompleted,
        onHoldMode,
        includeAcumatica,
        sourceFilter,
        binScopeIn,
      ] as const,
    /**
     * Per-order drill-down — `GET /api/sku-needs/orders-list/:salesOrderId/short-skus`.
     * Distinct from `orders` (per-SKU drill-down) by prefix.
     */
    orderShortSkus: (
      salesOrderId: string | null,
      warehouseId: string | undefined,
      universe: 'unallocated' | 'short' | 'both' | undefined,
      includeNonStock?: boolean,
      onHoldMode?: 'include' | 'only' | 'exclude',
      binScopeIn?: string,
    ) =>
      [
        'sku-needs',
        'order-short-skus',
        salesOrderId,
        warehouseId,
        universe,
        includeNonStock,
        onHoldMode,
        binScopeIn,
      ] as const,
  },

  // ------------------------------------------------------------------
  // Ship Schedule (SKU Needs → forward Acumatica shipment book)
  // ------------------------------------------------------------------
  // The filter surface is wide (18+ params), so — unlike `skuNeeds` above —
  // the params travel as ONE opaque object rather than a positional list. The
  // hooks always build that object from the same `buildShipScheduleQuery`
  // helper they send on the wire, so the cache key and the request cannot
  // drift apart.
  shipSchedule: {
    all: ['ship-schedule'] as const,
    /** By-SKU list — `GET /api/sku-needs/ship-schedule`. */
    skus: (params: unknown, warehouseId: string | undefined) =>
      ['ship-schedule', 'skus', params, warehouseId] as const,
    /** By-shipment list — `GET /api/sku-needs/ship-schedule/shipments`. */
    shipments: (params: unknown, warehouseId: string | undefined) =>
      ['ship-schedule', 'shipments', params, warehouseId] as const,
    /** Per-SKU ship-date ladder — `GET /api/sku-needs/ship-schedule/sku-ladder`. */
    skuLadder: (
      sku: string | null,
      params: unknown,
      warehouseId: string | undefined,
    ) => ['ship-schedule', 'sku-ladder', sku, params, warehouseId] as const,
    /** Per-shipment lines — `GET /api/sku-needs/ship-schedule/shipment-lines`. */
    shipmentLines: (
      shipmentNbr: string | null,
      params: unknown,
      warehouseId: string | undefined,
    ) =>
      [
        'ship-schedule',
        'shipment-lines',
        shipmentNbr,
        params,
        warehouseId,
      ] as const,
  },

  // ------------------------------------------------------------------
  // Slotting layout
  // ------------------------------------------------------------------
  slottingLayout: {
    all: ['slotting-layout'] as const,
    warehouse: (id: string | undefined) =>
      ['slotting-layout', 'warehouse', id] as const,
  },

  // ------------------------------------------------------------------
  // SO routing rules
  // ------------------------------------------------------------------
  soRoutingRules: {
    all: ['so-routing-rules'] as const,
    list: (params?: unknown) => ['so-routing-rules', params] as const,
    detail: (id: string | undefined) =>
      ['so-routing-rules', 'detail', id] as const,
    defaultWarehouse: () => ['so-routing-rules', 'default-warehouse'] as const,
  },

  // ------------------------------------------------------------------
  // SKU replacement rules (unified rules of type SKU_REPLACEMENT +
  // companion config; apply-to-open-orders preview/execute)
  // ------------------------------------------------------------------
  skuReplacementRules: {
    all: ['sku-replacement-rules'] as const,
    list: (params?: unknown) => ['sku-replacement-rules', params] as const,
    patternPreview: (
      sourcePattern: string,
      targetTemplate: string,
      warehouseId?: string,
    ) =>
      [
        'sku-replacement-rules',
        'pattern-preview',
        sourcePattern,
        targetTemplate,
        warehouseId,
      ] as const,
  },

  // ------------------------------------------------------------------
  // Item type overrides (persistent WMS-side stock/non-stock flip for
  // items mis-typed in Acumatica)
  // ------------------------------------------------------------------
  itemTypeOverrides: {
    all: ['item-type-overrides'] as const,
    list: () => ['item-type-overrides', 'list'] as const,
  },

  // ------------------------------------------------------------------
  // Roles
  // ------------------------------------------------------------------
  roles: {
    all: ['roles'] as const,
    list: () => ['roles', 'list'] as const,
    detail: (id: string | undefined) => ['roles', id] as const,
    routePermissions: ['roles', 'route-permissions'] as const,
  },

  // ------------------------------------------------------------------
  // Sync conflicts
  // ------------------------------------------------------------------
  syncConflicts: {
    all: ['sync-conflicts'] as const,
    list: (params?: unknown) => ['sync-conflicts', 'list', params] as const,
    detail: (id: string) => ['sync-conflicts', id] as const,
  },

  // ------------------------------------------------------------------
  // Sync dashboard
  // ------------------------------------------------------------------
  syncDashboard: {
    all: ['sync-dashboard'] as const,
    overview: () => ['sync-dashboard', 'overview'] as const,
  },

  // ------------------------------------------------------------------
  // Tags
  // ------------------------------------------------------------------
  tags: {
    all: ['tags'] as const,
    list: (search?: string) => ['tags', search] as const,
    suppressionPatterns: () => ['tags', 'suppression-patterns'] as const,
  },

  // ------------------------------------------------------------------
  // Transfer orders
  // ------------------------------------------------------------------
  transferOrders: {
    all: ['transfer-orders'] as const,
    list: (params?: unknown) => ['transfer-orders', params] as const,
    detail: (id: string | undefined) => ['transfer-orders', id] as const,
  },

  // ------------------------------------------------------------------
  // Replacement orders (customer service)
  // ------------------------------------------------------------------
  replacementOrders: {
    options: ['replacement-orders', 'options'] as const,
    lookup: (orderNumber: string | undefined) =>
      ['replacement-orders', 'lookup', orderNumber] as const,
    resolve: (
      customerOrderNumber: string | undefined,
      warehouseId: string | undefined,
    ) =>
      [
        'replacement-orders',
        'resolve',
        customerOrderNumber,
        warehouseId,
      ] as const,
  },

  // ------------------------------------------------------------------
  // UOM conversions
  // ------------------------------------------------------------------
  uomConversions: {
    all: ['uom-conversions'] as const,
    list: (params?: unknown) => ['uom-conversions', params] as const,
    detail: (id: string | undefined) => ['uom-conversions', id] as const,
    poUoms: () => ['uom-conversions', 'po-uoms'] as const,
    bySku: (params?: unknown) => ['uom-conversions', 'by-sku', params] as const,
  },

  // ------------------------------------------------------------------
  // Users
  // ------------------------------------------------------------------
  users: {
    all: ['users'] as const,
    me: () => ['users', 'me'] as const,
    stats: () => ['users', 'stats'] as const,
    lists: () => ['users', 'list'] as const,
    list: (filters: unknown) => ['users', 'list', filters] as const,
    detail: (id: string) => ['users', id] as const,
    warehouseAssignments: (userId: string) =>
      ['users', userId, 'warehouses'] as const,
    zoneAssignments: (userId: string, assignmentId: string) =>
      ['users', userId, 'warehouses', assignmentId, 'zones'] as const,
    effectiveAssignments: (userId: string) =>
      ['users', userId, 'assignments'] as const,
    /** Explicit per-user permissions (PR-E2). */
    grants: (userId: string) => ['users', userId, 'grants'] as const,
  },

  // ------------------------------------------------------------------
  // Vendors
  // ------------------------------------------------------------------
  vendors: {
    all: ['vendors'] as const,
    list: (filters?: unknown) => ['vendors', filters] as const,
    detail: (id: string | undefined) => ['vendors', id] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen projects (kitchen module K-6)
  // ------------------------------------------------------------------
  kitchenProjects: {
    all: ['kitchen-projects'] as const,
    lists: ['kitchen-projects', 'list'] as const,
    list: (params?: unknown) => ['kitchen-projects', 'list', params] as const,
    detail: (id: string | undefined) =>
      ['kitchen-projects', 'detail', id] as const,
    rows: (id: string | undefined) => ['kitchen-projects', 'rows', id] as const,
    revisions: (id: string | undefined) =>
      ['kitchen-projects', 'revisions', id] as const,
    revision: (id: string | undefined, revision: number | null) =>
      ['kitchen-projects', 'revision', id, revision] as const,
    /** Active label colors for the scheme editor / row overrides. */
    colors: ['kitchen-projects', 'colors'] as const,
    /** A project view under the filter bar (PR-5). */
    view: (id: string | undefined, view: string, params?: unknown) =>
      ['kitchen-projects', 'view', id, view, params] as const,
    /** Orders over the unit matrix (PR-8). */
    orders: (id: string | undefined) =>
      ['kitchen-projects', 'orders', id] as const,
    orderUnits: (id: string | undefined) =>
      ['kitchen-projects', 'order-units', id] as const,
    orderHistory: (id: string | undefined, orderId: string | null) =>
      ['kitchen-projects', 'order-history', id, orderId] as const,
    /** Kitchens → Acumatica (PR-O2): each order's Acumatica orders. */
    acumaticaOrders: (id: string | undefined) =>
      ['kitchen-projects', 'acumatica-orders', id] as const,
    /** LIVE: the project's PR orders in Acumatica (the link picker). */
    acumaticaHandMade: (id: string | undefined) =>
      ['kitchen-projects', 'acumatica-hand-made', id] as const,
    /** LIVE: one PR order typed by its number (under the list's key). */
    acumaticaHandMadeOne: (id: string | undefined, orderNbr: string | null) =>
      ['kitchen-projects', 'acumatica-hand-made', id, orderNbr] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen labels (multi-family cabinet lists)
  // ------------------------------------------------------------------
  kitchenLabels: {
    all: ['kitchen-labels'] as const,
    lists: ['kitchen-labels', 'list'] as const,
    list: (params?: unknown) => ['kitchen-labels', 'list', params] as const,
    detail: (id: string | undefined) =>
      ['kitchen-labels', 'detail', id] as const,
    /** Global roster (active LABEL_ZPL) - no warehouse dimension. */
    printers: ['kitchen-labels', 'printers'] as const,
    /** PR-S3b: the Legacy Labels switch + uploaded-list counts. */
    legacyStatus: ['kitchen-labels', 'legacy-status'] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen assembly station (kitchens end to end PR-L1)
  // ------------------------------------------------------------------
  kitchenAssembly: {
    all: ['kitchen-assembly'] as const,
    orders: (warehouseId: string | null) =>
      ['kitchen-assembly', 'orders', warehouseId] as const,
    order: (orderId: string | null, warehouseId: string | null) =>
      ['kitchen-assembly', 'order', orderId, warehouseId] as const,
    // PR-L3: the stage-close gap review of one order.
    review: (orderId: string | null, warehouseId: string | null) =>
      ['kitchen-assembly', 'review', orderId, warehouseId] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen missing parts + spares (kitchens end to end PR-M4 / PR-L3)
  // ------------------------------------------------------------------
  kitchenShortfalls: {
    all: ['kitchen-shortfalls'] as const,
    list: (params: unknown) => ['kitchen-shortfalls', 'list', params] as const,
    spares: (partKeys: string[]) =>
      ['kitchen-shortfalls', 'spares', partKeys] as const,
  },

  // ------------------------------------------------------------------
  // Kitchens → Acumatica bridge (kitchens end to end PR-L2)
  // ------------------------------------------------------------------
  kitchenAcumatica: {
    all: ['kitchen-acumatica'] as const,
    assemblyBatches: (warehouseId: string | null, status: string | null) =>
      ['kitchen-acumatica', 'assembly-batches', warehouseId, status] as const,
    // Assembled cabinets not batched on purpose (project not sent / no item).
    assemblyWaiting: (warehouseId: string | null) =>
      ['kitchen-acumatica', 'assembly-waiting', warehouseId] as const,
    // PR-S2: the Shipments tab.
    shipments: (warehouseId: string | null, status: string | null) =>
      ['kitchen-acumatica', 'shipments', warehouseId, status] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen shipping — the loading dock (kitchens end to end PR-S1)
  // ------------------------------------------------------------------
  kitchenShipping: {
    all: ['kitchen-shipping'] as const,
    orders: (warehouseId: string | null) =>
      ['kitchen-shipping', 'orders', warehouseId] as const,
    order: (orderId: string | null, warehouseId: string | null) =>
      ['kitchen-shipping', 'order', orderId, warehouseId] as const,
    loads: (warehouseId: string | null, status: string | null) =>
      ['kitchen-shipping', 'loads', warehouseId, status] as const,
    load: (loadId: string | null, warehouseId: string | null) =>
      ['kitchen-shipping', 'load', loadId, warehouseId] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen catalog (product database — kitchen module K-3)
  // ------------------------------------------------------------------
  kitchenCatalog: {
    all: ['kitchen-catalog'] as const,
    summary: ['kitchen-catalog', 'summary'] as const,
    cabinets: (params?: unknown) =>
      ['kitchen-catalog', 'cabinets', params] as const,
    cabinet: (id: string | undefined) =>
      ['kitchen-catalog', 'cabinet', id] as const,
    /** One key per entity list: types, colors, hardware items, part types, … */
    entity: (entity: string, params?: unknown) =>
      ['kitchen-catalog', 'entity', entity, params] as const,
    imports: ['kitchen-catalog', 'imports'] as const,
    importList: (params?: unknown) =>
      ['kitchen-catalog', 'imports', 'list', params] as const,
    importJob: (jobId: string | null) =>
      ['kitchen-catalog', 'imports', 'job', jobId] as const,
    referenceImportJob: (jobId: string | null) =>
      ['kitchen-catalog', 'reference-imports', 'job', jobId] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen engineering names + spec status (kitchens end to end PR-E2)
  // ------------------------------------------------------------------
  kitchenEngineeringNames: {
    all: ['kitchen-engineering-names'] as const,
    summary: ['kitchen-engineering-names', 'summary'] as const,
    mapIt: (params?: unknown) =>
      ['kitchen-engineering-names', 'map-it', params] as const,
    patterns: ['kitchen-engineering-names', 'patterns'] as const,
    overrides: ['kitchen-engineering-names', 'overrides'] as const,
    specStatus: (params?: unknown) =>
      ['kitchen-engineering-names', 'spec-status', params] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen engineering review (kitchen module K-9)
  // ------------------------------------------------------------------
  kitchenEngineering: {
    all: ['kitchen-engineering'] as const,
    queue: ['kitchen-engineering', 'queue'] as const,
    review: (id: string | undefined, all?: boolean) =>
      ['kitchen-engineering', 'review', id, !!all] as const,
    // Kitchen projects v2 PR-4: one line's parts for the Edit parts dialog.
    lineParts: (projectId: string | undefined, rowId: string | undefined) =>
      ['kitchen-engineering', 'line-parts', projectId, rowId] as const,
    // Kitchen projects v2 PR-2: before → after for "catalog changed since confirm".
    catalogChanges: (projectId: string | undefined) =>
      ['kitchen-engineering', 'catalog-changes', projectId] as const,
    // QA H-24: the review page's Parts view.
    parts: (projectId: string | undefined) =>
      ['kitchen-engineering', 'parts', projectId] as const,
  },

  // ------------------------------------------------------------------
  // Kitchen planning dashboard (kitchen module K-8). Any kitchen project or
  // row change invalidates `all` (plan §11).
  // ------------------------------------------------------------------
  kitchenPlanning: {
    all: ['kitchen-planning'] as const,
    demand: (params?: unknown) =>
      ['kitchen-planning', 'demand', params] as const,
    projects: (params?: unknown) =>
      ['kitchen-planning', 'projects', params] as const,
  },

  // ------------------------------------------------------------------
  // Kitchens → Production (kitchens end to end PR-M3, plan §6.5): the needs
  // views (by order / cabinet SKU / part / line), one order's tracking and
  // the create dialog's parts preview. Any kitchen order write invalidates
  // `all` (and the project caches).
  // ------------------------------------------------------------------
  kitchenProduction: {
    all: ['kitchen-production'] as const,
    // Kitchens end to end PR-M3: the needs views and one order's tracking.
    needs: (params?: unknown) =>
      ['kitchen-production', 'needs', params] as const,
    tracking: (orderId: string | null) =>
      ['kitchen-production', 'tracking', orderId] as const,
    partsPreview: (
      projectId: string | null,
      orderId: string | null,
      rowIds: readonly string[],
    ) =>
      [
        'kitchen-production',
        'parts-preview',
        projectId,
        orderId,
        rowIds,
      ] as const,
    /** The preview of a needs-dashboard selection (PR-M3) — same 'parts-preview' slot. */
    partsPreviewSelection: (scope: unknown) =>
      ['kitchen-production', 'parts-preview', 'selection', scope] as const,
  },

  // ------------------------------------------------------------------
  // Kitchens → Acumatica (PR-A2): a project's Acumatica SKUs, the
  // all-projects overview and the colour materials purchasing confirms.
  // ------------------------------------------------------------------
  kitchenErpBridge: {
    all: ['kitchen-erp-bridge'] as const,
    items: (projectId: string | undefined) =>
      ['kitchen-erp-bridge', 'items', projectId] as const,
    overview: ['kitchen-erp-bridge', 'overview'] as const,
    materials: ['kitchen-erp-bridge', 'materials'] as const,
    // H-35: the project's colours and their board / edgebanding; the type-ahead.
    projectColourMaterials: (projectId: string | undefined) =>
      ['kitchen-erp-bridge', 'colour-materials', projectId] as const,
    materialSearch: (q: string, role: string, live: boolean) =>
      ['kitchen-erp-bridge', 'material-search', q, role, live] as const,
    // R2: the recipes proposed from the quote, for purchasing to answer.
    recipeProposals: (projectId: string | undefined) =>
      ['kitchen-erp-bridge', 'recipe-proposals', projectId] as const,
    attention: ['kitchen-erp-bridge', 'attention'] as const,
    // The project's assembled cabinets not batched yet (late switch-on note).
    assemblyWaiting: (projectId: string | undefined) =>
      ['kitchen-erp-bridge', 'assembly-waiting', projectId] as const,
  },

  // ------------------------------------------------------------------
  // Warehouses
  // ------------------------------------------------------------------
  warehouses: {
    all: ['warehouses'] as const,
    allList: ['warehouses', 'all'] as const,
    list: (params?: unknown) => ['warehouses', params] as const,
    listAll: (params?: unknown) => ['warehouses', 'all', params] as const,
    detail: (id: string | undefined) => ['warehouses', id] as const,
    accessible: ['accessible-warehouses'] as const,
  },
} as const;
